import { Tooltip } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import { useMemo } from 'react';
import { bodyTypes, getLabel } from 'src/modules/labels';
import theme from 'src/setup/theme';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  count: {
    fontSize: 11,
    fontWeight: 500,
    color: grey[600],
    flexShrink: 0,
    minWidth: 16,
  },
  label: {
    fontSize: 12,
    color: grey[800],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
  },
  more: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    fontSize: 10,
    fontWeight: 500,
    color: theme.palette.primary.main,
    backgroundColor: grey[200],
    borderRadius: 4,
    padding: '1px 4px',
    cursor: 'default',
  },
});

const MAX_TYPES_DISPLAYED = 3;

interface PackageBodyTypesCellProps {
  types: { [key: string]: number };
}

export const PackageBodyTypesCell = ({ types }: PackageBodyTypesCellProps): JSX.Element => {
  const classes = useStyles();

  const entries = useMemo(
    () =>
      Object.entries(types)
        .map(([type, count]) => ({
          key: type,
          label: getLabel(bodyTypes, type),
          count,
        }))
        .sort((a, b) => b.count - a.count),
    [types],
  );

  const displayed = entries.slice(0, MAX_TYPES_DISPLAYED);
  const remaining = entries.slice(MAX_TYPES_DISPLAYED);
  const remainingTooltip = remaining.map((e) => `${e.label} (${e.count})`).join(', ');

  return (
    <div className={classes.container}>
      {displayed.map((entry) => (
        <div key={entry.key} className={classes.row}>
          <span className={classes.count}>{entry.count}</span>
          <Tooltip title={entry.label} arrow>
            <span className={classes.label}>{entry.label}</span>
          </Tooltip>
        </div>
      ))}
      {remaining.length > 0 && (
        <Tooltip title={remainingTooltip} arrow>
          <span className={classes.more}>+{remaining.length}</span>
        </Tooltip>
      )}
    </div>
  );
};
